import React, { useState } from 'react';
import { Center, Spinner } from '@chakra-ui/react';
import { PageSystemLayout } from 'src/components/molecules/PageSystemLayout';
import { EmptyState } from 'src/components/molecules/EmptyState';
import { Button } from 'src/components/atoms/Button';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useBrandTrackingConfigQuery,
  useSosCountriesQuery,
} from 'src/api/brandTracking';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { BrandTrackingDashboard } from 'src/features/BrandTracking/components/organisms/BrandTrackingDashboard';
import { BrandTrackingFilterBar } from 'src/features/BrandTracking/components/organisms/BrandTrackingFilterBar';
import { KeywordEditorDrawer } from 'src/features/BrandTracking/components/molecules/KeywordEditorDrawer';
import { TimeRangeMonths } from 'src/features/BrandTracking/utils/shareOfSearch';
import { NoSubscriptionPage } from '../NoSubscriptionPage';

type BrandTrackingPagesProps = {
  isSubscribed: boolean;
};

export const BrandTrackingPages: React.FC<BrandTrackingPagesProps> = ({
  isSubscribed,
}) => {
  if (!isSubscribed) {
    return <NoSubscriptionPage service="Brand Tracking" />;
  }

  return <BrandTrackingPage />;
};

const BrandTrackingPage: React.FC = () => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [timeRange, setTimeRange] = useState<TimeRangeMonths>(24);

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useBrandTrackingConfigQuery(customerId);
  const {
    data: countries = [],
    isLoading: isCountriesLoading,
    isError: isCountriesError,
  } = useSosCountriesQuery(customerId);

  const isLoading = isConfigLoading || isCountriesLoading;
  const isError = isConfigError || isCountriesError;
  const hasData =
    Boolean(config?.configured) &&
    config?.latestDataDate != null &&
    countries.length > 0;

  // Countries load async — fall back to the first one until the user picks.
  const effectiveLocationId =
    selectedLocationId ?? countries[0]?.locationId ?? null;

  return (
    <>
      <PageSystemLayout
        pageTitle="Brand Tracking"
        pageSubtitle="How often people search for your brand compared to your competitors"
        filterBar={
          hasData ? (
            <BrandTrackingFilterBar
              countries={countries}
              selectedLocationId={effectiveLocationId}
              onLocationChange={setSelectedLocationId}
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          ) : null
        }
        extraButtons={
          config?.configured ? (
            <Button
              variant="primary"
              buttonName="edit-tracked-keywords"
              onClick={() => setIsEditorOpen(true)}>
              Edit keywords
            </Button>
          ) : undefined
        }>
        {isLoading && (
          <Center py={20}>
            <Spinner speed="0.8s" size="xl" thickness="4px" />
          </Center>
        )}
        {!isLoading && isError && (
          <ErrorNotifBox
            title="Could not load brand tracking data"
            description="Something went wrong while loading your data. Please try again later."
          />
        )}
        {!isLoading && !isError && !hasData && (
          <EmptyState
            title="Your brand data is being collected"
            description="Search data for your brand and competitors is gathered daily from Google. This usually takes a day after your account is set up. If data still doesn't appear, the brand keyword may be specified incorrectly or may not have enough search data in Google — contact support to check it."
          />
        )}
        {!isLoading && !isError && hasData && config && (
          <BrandTrackingDashboard
            customerId={customerId}
            config={config}
            selectedLocationId={effectiveLocationId}
            timeRange={timeRange}
          />
        )}
      </PageSystemLayout>

      {config && (
        <KeywordEditorDrawer
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          customerId={customerId}
          config={config}
          onSuccess={(message) => showSuccess('Keywords updated', message)}
          onError={(message) => showError('Could not update keywords', message)}
        />
      )}

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </>
  );
};
